import Constants from "expo-constants";
import { Platform } from "react-native";

export type DeploymentEnvironment = "local" | "preview" | "production";

function resolveDeploymentEnvironment(): DeploymentEnvironment {
  if (Platform.OS === "web") {
    if (typeof window === "undefined") return "local";
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") return "local";
    // Cloudflare Workers: use.<domain> is production, dev-use.<domain> (and
    // any *.workers.dev URL) is preview.
    if (host.startsWith("dev-use.") || host.endsWith(".workers.dev"))
      return "preview";
    return "production";
  }
  // Native builds: `ENVIRONMENT` in .env (or the eas.json build profile) reaches
  // the app through app.config.ts. DEVELOPMENT is the local DDEV stack, TESTING
  // the Coolify preview API; anything else, unset included, is production.
  const requested = String(
    Constants.expoConfig?.extra?.environment ?? "",
  ).toUpperCase();
  return requested === "DEVELOPMENT"
    ? "local"
    : requested === "TESTING"
      ? "preview"
      : "production";
}

const environment = resolveDeploymentEnvironment();
const siteUrls: Record<DeploymentEnvironment, string> = {
  local: "http://localhost:3000",
  preview: "https://taskscribe-website.pages.dev",
  production: "https://www.taskscribe.app",
};
const appUrls: Record<DeploymentEnvironment, string> = {
  local: "http://localhost:8081",
  preview: "https://dev-use.taskscribe.app",
  production: "https://use.taskscribe.app",
};

export const DEPLOYMENT_ENVIRONMENT = environment;
/** Whether new accounts can be created from the app. */
export const REGISTRATION_ENABLED = true;
export const SITE_URL = siteUrls[environment];
export const APP_URL = appUrls[environment];
export const SENTRY_APP_DSN =
  environment === "local"
    ? undefined
    : "https://38dab0a31a16c68b84a3f6df1646243e@o4504087623761920.ingest.us.sentry.io/4511953515970560";
// No spam check locally: the widget is not authorised for localhost.
export const TURNSTILE_SITE_KEY =
  environment === "local" ? "" : "0x4AAAAAAEsjJr_Mrr2RruZu";

// The API origin is public, so it is baked in. Local development talks to the DDEV site
// (`ddev start` in taskscribe-api); override with EXPO_PUBLIC_API_URL, e.g. for a phone on the LAN.
const apiUrls: Record<DeploymentEnvironment, string> = {
  local: "https://taskscribe-api.ddev.site",
  preview: "https://dev-api.taskscribe.app",
  production: "https://api.taskscribe.app",
};
export const API_URL =
  process.env.EXPO_PUBLIC_API_URL?.trim() || apiUrls[environment];
export const backendConfigured = Boolean(API_URL);
export const SENTRY_ENABLED = Boolean(SENTRY_APP_DSN);
export const TURNSTILE_ENABLED = Boolean(TURNSTILE_SITE_KEY);

// RevenueCat Web Billing (Stripe): live keys on production, sandbox keys (Stripe
// test cards) everywhere else.
const REVENUECAT_WEB_KEYS = {
  sandbox: "rcb_sb_XCSWndOQJJzZSPQmNPnpCGibM",
  production: "rcb_USaGDqeuaHOoqwTenhLzzgxRUpdf",
} as const;

export const REVENUECAT_API_KEYS = {
  ios: "appl_gfiNjDkvyJTabDGklLUIZdlDFPF",
  android: "goog_UsKFwAhqmghrwydbXIWhMBlJrPQ",
  test: "test_AkFWcqOJfCCVoNgTZLMbrXOpjGv",
  web:
    DEPLOYMENT_ENVIRONMENT === "production"
      ? REVENUECAT_WEB_KEYS.production
      : REVENUECAT_WEB_KEYS.sandbox,
} as const;
export const DEFAULT_REGIONAL_SETTINGS = {
  units: "metric",
  dateFormat: "en-NZ",
  timezone: "Pacific/Auckland",
} as const;
export const STORE_LINKS = { ios: "", android: "" } as const;
export const EXTERNAL_LINKS = {
  privacyPolicy: `${SITE_URL}/legal/privacy-policy/`,
  terms: `${SITE_URL}/legal/terms-and-conditions/`,
  facebook: "",
  instagram: "",
  help: `${SITE_URL}/help/`,
} as const;
